import React from 'react';
import { get, has, keyBy, filter, map, includes, toUpper } from 'lodash';
import styled from 'styled-components';
import { Switch } from 'antd';
import {
    Text,
    StatusTag,
    Number,
    FlexContainer,
    GetUnitPrice,
    Icon,
    Image,
    ConditionalRender,
    If,
    AppIcon, Column,
    ExcessTotal
} from '../../../../../../components/common';
import { formatRefId, productPickingType } from '../../../../../../utils/misc';
import { generateColumns } from './AuditColumnsGenerator';
import { substituteColumns, excessColumns } from './AuditTableColumns';
import './audit_switch.css';

const TagBase = styled.div`
    height: 20px;
    border-radius: 4px;
    color: white;
    padding: 4px 8px;
    display: inline-block;
    background: #166DC2;
    border: 1px solid #166DC2;
`;

const ClickableLink = styled.a`
    color: black;
`;

const _renderCell = (item, dataIndex) => (get(item, dataIndex, '-'));

const parentColumns = [
    {
        title: 'Imagen',
        dataIndex: 'imageUrl',
        key: 'imageUrl',
        index: 1,
        styled:{width:"75px", height:"75px"},
        render: (item, dataindex) => {
            return <Image src={get(item, 'imageUrl')} style={{borderWidth:'1px solid #B6C1C8', borderRadius:"8px"}}  />;
        }
    },
    {
        title: 'Producto',
        dataIndex: 'name',
        width: 150,
        key: 'product',
        index: 2,
        render: (item, dataIndex, { onRowClick = () => (null) }) => (
            <div>
                <ClickableLink onClick={(e) => { e.preventDefault(); onRowClick(item); }}>
                    <p>{get(item, dataIndex)}</p>
                </ClickableLink>
                <FlexContainer mY={5} align='center'>
                    <Text text={get(item, 'ean', 'No Disponible')} color='#666666' size={11} lineHeight={1} />
                </FlexContainer>
                <ConditionalRender condition={has(item, 'priceTags.0.rateAndBenefit.name')}>
                    <div style={{ display: 'flex' }}>
                        <AppIcon name='ic-descuento' fill='#666666' style={{ marginRight: '3px', fontSize: '11px' }} />
                        <Text text={get(item, 'priceTags.0.rateAndBenefit.name')} tiny color='#666666' />
                    </div>
                </ConditionalRender>
            </div>
        )
    },
    {
        title: 'Marca',
        dataIndex: 'brandName',
        key: 'brandName',
        index: 3,
        columnStyle: { textAlign: 'left' },
        render: (item, dataIndex) => <Text text={get(item, dataIndex, '')} size={11} />
    },
    {
        title: 'Solicitado',
        dataIndex: 'quantity',
        key: 'quantity',
        index: 4,
        render: (item, dataIndex) => {
            const quantity = get(item, dataIndex, '0');
            const unitMultiplier = get(item, 'unitMultiplier', '0');
            if (String(quantity) === '0') return _renderCell(null, null);
            const calculatedQuantity = quantity * parseFloat(unitMultiplier);
            const measureUnit = get(item, 'unit', 'un');
            const valueToShow = measureUnit === 'kg' ? parseFloat(calculatedQuantity).toFixed(3) : calculatedQuantity;
            return `${String(valueToShow).replace('.', ',')} ${measureUnit}`;
        }
    },
    {
        title: 'Resultado Picking',
        dataIndex: 'picking.pickingQuantity',
        index: 5,
        key: 'picked',
        render: (item, dataIndex) => {
            const status = get(item, 'picking.status');
            const shouldHide = ['FREE_PICKING_ACCEPTED', 'SUBSTITUTE_CONFIRMED'];
            const quantity = get(item, dataIndex, '0');
            const measureUnit = get(item, 'unit', 'un');
            const value = get(item, dataIndex);
            const valueToShow = measureUnit === 'kg' ? parseFloat(value).toFixed(3) : value;
            if (String(quantity) === '0' || shouldHide.includes(status)) return _renderCell(null, null);
            return `${(String(valueToShow).replace('.', ','))} ${measureUnit}`;
        }
    },
    {
        title: 'Stock Disponible',
        dataIndex: 'stock',
        index: 6,
        key: 'picked',
        render: (item, dataIndex, ctx) => {
            const stockMap = get(ctx, 'stockMap', {});
            const stock = parseInt(get(stockMap, `${formatRefId(get(item, 'refId', ''))}.stock`));
            return <Text size={11} text={`${stock || '0'} ${toUpper(get(item, 'unit', 'un'))}`} strong />;
        }
    },
    {
        title: 'Estado',
        dataIndex: 'status',
        key: 'status',
        index: 7,
        render: (item, dataIndex) => {
            const status = productPickingType(get(item, 'picking.status', get(item, 'status')));
            return (
                <FlexContainer center>
                    <StatusTag color='#1fa02e' key={dataIndex} status={`order_detail.item.picking.status.${status}`} tagType={status} />
                </FlexContainer>
            );
        }
    },
    {
        title: 'Condición',
        dataIndex: 'picking.productId.info',
        key: 'audit-rules',
        index: 8,
        render: (item, dataIndex, { auditInfo }) => {
            const productId = get(item, 'picking.id');
            const auditResults = get(keyBy(get(auditInfo, 'results.failed'), 'productId'), productId);
            const filteredAudits = filter(get(auditResults, 'audits'), ({ decision, smartrules }) => (smartrules && smartrules.length > 0) || decision);
            return (
                <FlexContainer direction='column'>
                    {map(filteredAudits, ({ smartrules, codeRule, decision }, idx) => {
                        const hasValidSmartRules = smartrules && smartrules.length > 0;
                        if (hasValidSmartRules) {
                            return smartrules.map(({ reason, subReasons }, sIdx) =>
                                subReasons?.length > 0 ? subReasons.map(
                                    (text, tIdx) => (
                                        <Text key={`${idx}-${sIdx}-${tIdx}`} lineHeight={2.5} size={11} text={text} />
                                    )) :
                                    <Text key={`${idx}-${sIdx}`} lineHeight={2.5} size={11} text={reason} />
                            );
                        } else if (decision) {
                            return (
                                <StatusTag
                                    mL={0}
                                    key={idx}
                                    status={`order_audit.status.${codeRule}`}
                                    tagType='error'
                                />
                            );
                        } else {
                            return <Text key={idx} lineHeight={2.5} size={11} text="-" />;
                        }
                    })}
                </FlexContainer>
            );
        }
    },
    {
        title: 'Diferencia Precio',
        dataIndex: 'picking.excess',
        key: 'excess',
        index: 9,
        render: (item, dataIndex) => {
            if (item?.picking?.substitution?.length === 1) {
                return <Text text={'-'} />
            }
            return <FlexContainer center>
                {get(item, dataIndex) && <ExcessTotal item={get(item, dataIndex)} />}
            </FlexContainer>
        }
    },
    {
        title: 'Precio Unitario',
        dataIndex: 'basePrice',
        key: 'client',
        index: 10,
        render: item => (
            <GetUnitPrice item={item}>
                {({ priceTags, listPrice, price }) => (
                    <>
                        {priceTags && (
                            <div>
                                <span>
                                    <del style={{ color: '#666666' }}><Number value={listPrice} /></del>
                                </span>
                            </div>
                        )}
                        <div>
                            <span>
                                <Number value={price} />
                            </span>
                        </div>
                    </>
                )}
            </GetUnitPrice>
        )
    },
    {
        title: 'Total Solicitado',
        dataIndex: 'sellingPrice',
        index: 11,
        key: 'sale_price',
        render: (item, dataIndex) => {
            const { quantity = 0 } = item;
            return (
                <div>
                    <span>
                        <Number value={get(item, dataIndex) * parseFloat(quantity)} />
                    </span>
                </div>
            );
        }
    },
    {
        title: 'Total Pickeado',
        dataIndex: 'picking',
        key: 'final_price',
        index: 12,
        render: (item, dataIndex) => {
            const data = get(item, dataIndex, {});
            const { sellingPrice, pickingQuantity, unitMultiplier } = data;
            const value = parseInt(sellingPrice) * (parseFloat(pickingQuantity) / parseFloat(unitMultiplier));
            return (
                <div>
                    <strong>
                        { value > 0 ? <Number value={value} /> : '-' }
                    </strong>
                </div>
            );
        }
    },
    {
        title: 'Validación',
        dataIndex: 'picking.productId.validate',
        key: 'validate-item',
        index: 13,
        render: (item, dataIndex, ctx) => {
            const id = get(item, 'picking.productId', get(item, 'productId'));
            const refId = formatRefId(get(item, 'refId', ''));
            const { formIsSubmitted, formValues = {}, onChangeValue, stockMap = {} } = ctx;
            const switchOptions = [{ value: 1, label: 'Aprobado' }, { value: 0, label: 'Rechazado' }];
            const isSplit = get(item, 'picking.split');
            const stock = parseInt(get(stockMap, `${refId}.stock`));

            const defaultRejected = (stock, hasHandler) => {
                if (isSplit) {
                    return false
                } else {
                    if (includes(['PARTIAL', 'PARTIAL_ACCEPTED', 'OUT_STOCK', 'OUT_STOCK_ACCEPTED'], get(item, 'picking.status'))) {
                        const originalQuantity = parseFloat(get(item, 'quantity', '0')) * parseFloat(get(item, 'unitMultiplier', '0'));
                        const missingQuantity = originalQuantity - parseFloat(get(item, 'picking.pickingQuantity'));
                        if ((missingQuantity > 0) && (parseFloat(stock) > missingQuantity)) {
                            if (hasHandler) return onChangeValue({ name: id, value: switchOptions[1] });
                            return true;
                        }
                    }
                }
            };

            return (
                <If
                    condition={formIsSubmitted}
                    then={(
                        <FlexContainer direction='column'>
                            <Text text={get(formValues, `${id}`, true) ? 'Aprobado' : 'Rechazado'} />
                        </FlexContainer>
                    )}
                    else={(
                        <Column height='100%' jsutify='center' gap={10}>
                            {isSplit && <TagBase>
                                <FlexContainer align='center' height='100%'>
                                    Split Order
                                </FlexContainer>
                            </TagBase>}
                            <Switch
                                defaultChecked={!defaultRejected(stock)}
                                checkedChildren='Aprobado'
                                unCheckedChildren='Rechazado'
                                name={id}
                                onChange={(value) => {
                                    if (value) return onChangeValue({ name: id, value: switchOptions[0] });
                                    return onChangeValue({ name: id, value: switchOptions[1] });
                                }}
                            />
                        </Column>
                    )}
                />
            );
        }
    }
];

const compoundColumns = substituteColumns.map(column => {
    if (column.key === 'excess' || column.dataIndex === 'picking.excess') {
        return {
            ...column,
            render: () => <Text text={'-'} />
        };
    }
    return column;
});

export const getColumns = () => {
    const columns = generateColumns([
        {
            key: 'parentColumns',
            scheme: [1, 2, 4, 5, 6, 10, 11, 12, 9, 7, 8, 13],
            columnSet: parentColumns
        },
        {
            key: 'excessColumns',
            scheme: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1],
            columnSet: excessColumns
        },
        {
            key: 'compoundColumns',
            scheme: [1, 2, 4, 5, 6, 10, 11, 12, 9, 7, 0, 0],
            columnSet: compoundColumns
        },
        {
            key: 'substituteColumns',
            scheme: [1, 2, 4, 5, 6, 10, 11, 12, 9, 7, 0, 0],
            columnSet: substituteColumns
        }
    ]);

    return columns;
};